import { useCallback, useMemo, useState } from 'react';
import { useLingui } from '@lingui/react/macro';
import { ph } from '@lingui/core/macro';

import { SportType } from 'modules/core/domain/enums/sport-type';
import { useOrganizer } from 'modules/organizer/ui/hooks/use-organizer';
import { Team } from 'modules/teams/domain/interfaces/team';
import { useLazyListTeamsQuery } from 'modules/teams/store/teams-api';
import { TeamOption, TeamSelectOption } from 'modules/teams/ui/components/team-async-select';
import { UDAsyncSelect } from 'ud-form/components/select/component';
import UDButton from 'ud-ui/components/button';
import Modal from 'ud-ui/components/modal';

/** Опция выдачи: недоступную команду react-select покажет, но выбрать не даст. */
export type TeamPickerOption = TeamSelectOption & { isDisabled?: boolean };

/** Сколько команд показывать в выдаче — остальные пользователь дофильтрует вводом. */
const SEARCH_LIMIT = 10;

/**
 * С какой длины ввода искать. По одному-двум символам совпадений столько, что выдача
 * бесполезна, а запрос уходит на каждое нажатие — поэтому ждём осмысленный кусок названия.
 */
const MIN_SEARCH_LENGTH = 3;

interface TeamPickerModalProps {
  title: string;
  subtitle?: string;
  /** Подпись кнопки подтверждения; отмена подписана одинаково везде. */
  submitLabel: string;
  /** Разрешить выбрать несколько команд за раз. */
  isMulti?: boolean;
  /** Команды, которые в выдаче видны, но выбрать их нельзя (уже добавлены). */
  disabledTeamIds?: number[];
  /** Пометка рядом с недоступной командой — почему её нельзя выбрать. */
  disabledHint?: string;
  /**
   * Выбранные команды. Пока промис не разрешится, кнопка в состоянии загрузки; отказ
   * оставляет окно открытым с прежним выбором — закрывает его вызывающая сторона.
   */
  onSubmit: (teams: Team[]) => void | Promise<void>;
  onClose: () => void;
}

/**
 * Выбор команды организации в модальном окне.
 *
 * Общая часть двух сценариев — добавления команд в заявки соревнования и добавления
 * игрока в команду. Одинаково в обоих ровно то, что раньше разъезжалось:
 *
 * - список команд организации виден **сразу при открытии**, без ввода: иначе селект
 *   встречает «Нет совпадений», хотя команды есть, их просто ещё не о чем фильтровать;
 * - уже добавленная команда показана, но отключена с пометкой (`disabledTeamIds`);
 * - высоту окна задаёт выдача селекта, а не фиксированная высота тела: пустое окно
 *   ровно по содержимому, а меню рисуется поверх него (`noOverflow`).
 *
 * Что делать с выбором, знает вызывающая сторона: `onSubmit` получает сами команды.
 * Монтировать компонент нужно только на время показа — закрытие сбрасывает выбор и кэш
 * опций, иначе только что добавленная команда осталась бы в списке доступных.
 */
export function TeamPickerModal({
  title,
  subtitle,
  submitLabel,
  isMulti = false,
  disabledTeamIds,
  disabledHint,
  onSubmit,
  onClose,
}: TeamPickerModalProps) {
  const { t } = useLingui();
  const { info } = useOrganizer();
  const [searchTeams] = useLazyListTeamsQuery();

  const [selected, setSelected] = useState<TeamPickerOption[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const disabledIds = useMemo(() => new Set(disabledTeamIds ?? []), [disabledTeamIds]);

  const loadOptions = useCallback(
    async (inputValue: string) => {
      if (!info?.id) return [];

      // `true` — preferCacheValue: повторное открытие модалки с тем же вводом не идёт в сеть.
      const teams = await searchTeams(
        {
          tournamentId: info.id,
          limit: SEARCH_LIMIT,
          offset: 0,
          sportType: info.sportType ?? SportType.football,
          namePart: inputValue,
        },
        true,
      ).unwrap();

      return teams.map((team) => {
        const isDisabled = disabledIds.has(team.id);

        return {
          value: team.id,
          data: team,
          isDisabled,
          label: (
            <span className="flex w-full items-center justify-between">
              <TeamOption team={team} />
              {isDisabled && disabledHint && <span className="text-surfaceRomanSilver30 ml-4 shrink-0">{disabledHint}</span>}
            </span>
          ),
        } as TeamPickerOption;
      });
    },
    [info, disabledIds, disabledHint, searchTeams],
  );

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      await onSubmit(selected.map((option) => option.data!));
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, selected]);

  return (
    <Modal show onClose={onClose} size="md" noOverflow>
      <Modal.Header title={title} subtitle={subtitle} />

      <Modal.Body>
        <UDAsyncSelect<TeamPickerOption, boolean>
          isMulti={isMulti}
          preloadOptions
          closeMenuOnSelect={!isMulti}
          hideSelectedOptions={false}
          emptyInputMessage={t`У организации пока нет команд — сначала создайте команду в разделе «Команды».`}
          minSearchLength={MIN_SEARCH_LENGTH}
          minSearchMessage={t`Введите не меньше ${ph({ min: MIN_SEARCH_LENGTH })} символов, чтобы найти команду`}
          placeholder={t`Найти команду по названию`}
          loadOptions={loadOptions}
          value={isMulti ? selected : (selected[0] ?? null)}
          onChange={(value) => setSelected(value ? (Array.isArray(value) ? [...value] : [value as TeamPickerOption]) : [])}
        />
      </Modal.Body>

      <Modal.Footer>
        <UDButton variant="secondary" onClick={onClose} disabled={isSubmitting}>
          {t`Отмена`}
        </UDButton>
        <UDButton variant="primary" onClick={handleSubmit} loading={isSubmitting} disabled={selected.length === 0}>
          {submitLabel}
        </UDButton>
      </Modal.Footer>
    </Modal>
  );
}
